import type { SubmitHandler } from 'react-hook-form';
import { useState } from 'react';
import { faFolder } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { isArray, isString } from 'lodash';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { IApiUpdateCategoryResponseBody } from '~/lib/api/modules/Categories';
import type { IUseUpdateCategoryMutationValues } from '~/lib/api/modules/Categories/hooks/useUpdateCategoryMutation';
import type { ILinkFormValues } from './LinkForm';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { useNavigate } from '~/components/utils/Navigation';
import { getActionTypeFromTarget } from '~/helpers/getActionTypeFromTarget';
import { getCategoryActionFromType } from '~/helpers/getCategoryActionFromType';
import { useCategoryQuery } from '~/lib/api/modules/Categories/hooks/useCategoryQuery';
import { useUpdateCategoryMutation } from '~/lib/api/modules/Categories/hooks/useUpdateCategoryMutation';
import { LinkForm } from './LinkForm';

export type IUpdateLinkFormProps = IContainerProps & {
  onSuccess?: (
    outputData: IApiUpdateCategoryResponseBody,
    inputValues: IUseUpdateCategoryMutationValues,
  ) => void;
  cancelUrl?: string;
  appId: string;
  categoryId: string;
};

export const UpdateLinkForm: React.FC<IUpdateLinkFormProps> = (props) => {
  const { sx, onSuccess, cancelUrl, appId, categoryId } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const linkQuery = useCategoryQuery(
    { appId, categoryId },
    {
      onSuccess: (category) => {
        if (!category.action) {
          navigate(`/apps/${appId}/categories/${categoryId}/update`, {
            replace: true,
          });
        }
      },
    },
  );
  const updateLinkMutation = useUpdateCategoryMutation();
  const [isDraft, setIsDraft] = useState(false);

  const link = linkQuery.data;
  const actionType = link?.action
    ? getActionTypeFromTarget(link.action)
    : undefined;
  const action = actionType
    ? getCategoryActionFromType(actionType, t)
    : undefined;
  const rawActionTarget = link?.action?.replace(action?.prefix ?? '', '');
  const actionTarget =
    actionType === 'pdf'
      ? decodeURIComponent(rawActionTarget || '')
      : rawActionTarget;

  const handleSubmit: SubmitHandler<ILinkFormValues> = (values) => {
    if (updateLinkMutation.isLoading) {
      return;
    }

    return updateLinkMutation.mutateAsync(
      {
        appId,
        categoryId,
        body: {
          name: values.name ?? '',
          picture: values.pictures?.length ? [values.pictures[0].id] : [],
          parentId: values.parentId ? values.parentId : undefined,
          action: `${values.actionPrefix ?? ''}${values.actionTarget}`,
          hidden: values.isDraft ?? false,
          isEvent: false,
          reversedFlow: false,
          badges: values.userBadges,
          badgesAllow: values.requireAllUserBadges ? 'all' : 'any',
        },
      },
      { onSuccess },
    );
  };

  return (
    <ContentBlock draft={isDraft} sx={sx} draftOutlineSize="lg">
      <ContentBlock.Title icon={<FontAwesomeIcon icon={faFolder} />}>
        {t('UpdateLinkForm.edit-a-link')}{' '}
        {isDraft ? t('UpdateLinkForm.not-published-yet') : null}
      </ContentBlock.Title>

      <ContentBlock.Suspense
        loading={linkQuery.isLoading}
        error={linkQuery.error}
      >
        {link ? (
          <LinkForm
            mode="edition"
            appId={appId}
            categoryId={categoryId}
            cancelUrl={cancelUrl}
            defaultValues={{
              name: link.name,
              pictures: link.picture
                ? isArray(link.picture)
                  ? isString(link.picture[0])
                    ? [{ type: 'picture', id: link.picture[0] }]
                    : undefined
                  : isString(link.picture)
                    ? [{ type: 'picture', id: link.picture }]
                    : undefined
                : undefined,
              parentId: link.parentId ?? '',
              actionType: actionType ?? '',
              actionPrefix: action?.prefix ?? '',
              actionTarget: actionTarget ?? '',
              isDraft: !!link.hidden,
            }}
            onSubmit={handleSubmit}
            onDraftStatusChange={setIsDraft}
          />
        ) : null}
      </ContentBlock.Suspense>
    </ContentBlock>
  );
};
